import React, { useContext, useEffect, useMemo, useState } from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Box,
  Button,
  Collapse,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { Link } from 'react-router-dom';

import { Alerta } from '../../components/alerta';
import PageHeader from '../../components/pageHeader';
import SubmitButton from '../../components/submitButton';
import DadosUsuario from '../../utils/context';
import { fetchFlask } from '../../utils/fetchFlask';
import { capitalizeFirstLetter } from '../../utils/stringFormat';

const OrdersManager = () => {
  const dadosUsuario = useContext(DadosUsuario);
  const autorizacoes = dadosUsuario.autorizacoes;

  const [rows, setRows] = useState([]);
  const [expandedOrders, setExpandedOrders] = useState({});
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('');
  const [openAlerta, setOpenAlerta] = useState(false);
  const [editedRows, setEditedRows] = useState([]);
  const [isSubmit, setIsSubmit] = useState(false);
  const [filters, setFilters] = useState({
    pedidoID: '',
    site: '',
    nome: '',
    dataPedido: '',
    status: '',
    dataAlteracao: '',
    material: '',
    quantidade: '',
    obs: ''
  });

  const canEditStatus = autorizacoes.includes('PedidosOeMGerencial') || autorizacoes.includes('PedidosOeMTecnico');
  const canEditObs = autorizacoes.includes('PedidosOeMGerencial');

  const statusOptions = autorizacoes.includes('PedidosOeMGerencial')
    ? ['PENDENTE', 'EM COTAÇÃO', 'EM TRANSPORTE', 'EM ESTOQUE', 'COMPRA DIRETA', 'CANCELADO', 'FINALIZADO']
    : ['PENDENTE', 'FINALIZADO'];

  useEffect(() => {
    async function getOrders () {
      const response = await fetchFlask('getOrders');
      if (response.status !== 'error') {
        setRows(response.orders);
      } else {
        setMessage(response.message);
        setStatus(response.status);
        setOpenAlerta(true);
      }
    }
    getOrders();
  }, []);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const filteredRows = useMemo(() => rows.filter((row) =>
    Object.keys(filters).every((key) =>
      String(row[key] || '')
        .toLowerCase()
        .includes(filters[key].toLowerCase())
    )
  ), [rows, filters]);

  const groupedOrders = useMemo(() => {
    const map = new Map();

    filteredRows.forEach((row) => {
      const orderId = row.pedidoID;
      if (!map.has(orderId)) {
        map.set(orderId, {
          pedidoID: orderId,
          site: row.site,
          nome: row.nome,
          dataPedido: row.dataPedido,
          items: []
        });
      }

      map.get(orderId).items.push(row);
    });

    return Array.from(map.values());
  }, [filteredRows]);

  const summarizeField = (items, field) => {
    const values = Array.from(new Set(items.map((item) => String(item[field] ?? '').trim()).filter(Boolean)));
    if (values.length === 0) return '-';
    if (values.length === 1) return values[0];
    return 'Vários';
  };

  const handleToggleOrder = (pedidoID) => {
    setExpandedOrders((prev) => ({ ...prev, [pedidoID]: !prev[pedidoID] }));
  };

  const registerEditedRow = (updatedRow) => {
    setEditedRows((prev) => {
      const exists = prev.some((row) => row.id === updatedRow.id);
      if (exists) {
        return prev.map((row) => (row.id === updatedRow.id ? updatedRow : row));
      }
      return [...prev, updatedRow];
    });
  };

  const updateRowField = (rowId, field, value) => {
    let updatedTarget = null;

    setRows((prevRows) => prevRows.map((row) => {
      if (row.id !== rowId) return row;

      const updatedRow = { ...row, [field]: value };
      updatedTarget = updatedRow;
      return updatedRow;
    }));

    const originalRow = rows.find((row) => row.id === rowId);
    if (!originalRow || !updatedTarget) return;

    if (originalRow.status !== updatedTarget.status || originalRow.obs !== updatedTarget.obs) {
      registerEditedRow(updatedTarget);
    }
  };

  async function submitUpdateOrders (e) {
    e.preventDefault();
    setIsSubmit(true);

    if (!editedRows || editedRows.length < 1) {
      setMessage('Altere algum item para atualizar o pedido');
      setStatus('warning');
      setOpenAlerta(true);
      setIsSubmit(false);
      return;
    }

    const body = JSON.stringify({ editedRows });

    const response = await fetchFlask('updateOrders', body);
    if (response.status === 'success') {
      setEditedRows([]);
    }
    setMessage(response.message);
    setStatus(response.status);
    setOpenAlerta(true);
    setIsSubmit(false);
  }

  return (
    <Box className='mainBox'>
      <Alerta
        texto={message}
        status={status}
        open={openAlerta}
        handleClose={() => setOpenAlerta(false)}
      />

      <PageHeader>Pedidos - Gestão</PageHeader>

      <Link to='/'>
        <Button>Voltar</Button>
      </Link>

      {['PedidosOeMGerencial', 'PedidosOeMTecnico', 'Adm'].some(a => autorizacoes.includes(a)) && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant='subtitle1' gutterBottom>
            Filtros
          </Typography>
          <Box display='grid' gridTemplateColumns='repeat(auto-fit, minmax(150px, 1fr))' gap={2}>
            {Object.keys(filters).map((field) => (
              <TextField
                key={field}
                label={capitalizeFirstLetter(field)}
                variant='outlined'
                size='small'
                value={filters[field]}
                onChange={(e) => handleFilterChange(field, e.target.value)}
              />
            ))}
          </Box>
        </Paper>
      )}

      <form action='' method='POST' id='formUpdateOrders' onSubmit={submitUpdateOrders}>
        <Paper sx={{ p: 2 }}>
          <TableContainer sx={{ maxHeight: 560 }}>
            <Table stickyHeader size='small'>
              <TableHead>
                <TableRow>
                  <TableCell width={64} />
                  <TableCell>ID</TableCell>
                  <TableCell>Site</TableCell>
                  <TableCell>Nome</TableCell>
                  <TableCell>Data Pedido</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Data Alteração</TableCell>
                  <TableCell>Material</TableCell>
                  <TableCell>Quantidade</TableCell>
                  <TableCell>Observação</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {groupedOrders.map((order) => {
                  const expanded = !!expandedOrders[order.pedidoID];
                  return (
                    <React.Fragment key={order.pedidoID}>
                      <TableRow
                        sx={(theme) => ({
                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.045)' : '#f6f8fb',
                          '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#eef2f8'
                          }
                        })}
                        hover
                      >
                        <TableCell>
                          <IconButton size='small' onClick={() => handleToggleOrder(order.pedidoID)}>
                            {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>{order.pedidoID}</TableCell>
                        <TableCell>{order.site || '-'}</TableCell>
                        <TableCell>{order.nome || '-'}</TableCell>
                        <TableCell>{order.dataPedido || '-'}</TableCell>
                        <TableCell>{summarizeField(order.items, 'status')}</TableCell>
                        <TableCell>{summarizeField(order.items, 'dataAlteracao')}</TableCell>
                        <TableCell>Vários</TableCell>
                        <TableCell>Vários</TableCell>
                        <TableCell>{summarizeField(order.items, 'obs')}</TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
                          <Collapse in={expanded} timeout='auto' unmountOnExit>
                            <Box sx={(theme) => ({
                              p: 1.5,
                              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(10, 12, 43, 0.64)' : '#fff'
                            })}>
                              <Typography variant='subtitle2' sx={{ mb: 1 }}>
                                Itens do Pedido #{order.pedidoID}
                              </Typography>

                              <Table size='small'>
                                <TableHead>
                                  <TableRow>
                                    <TableCell>ID Item</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Data Alteração</TableCell>
                                    <TableCell>Material</TableCell>
                                    <TableCell>Quantidade</TableCell>
                                    <TableCell>Observação</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {order.items.map((item) => (
                                    <TableRow key={item.id} hover>
                                      <TableCell>{item.id}</TableCell>
                                      <TableCell width={220}>
                                        {canEditStatus
                                          ? (
                                            <Select
                                              size='small'
                                              fullWidth
                                              value={item.status || ''}
                                              onChange={(e) => updateRowField(item.id, 'status', e.target.value)}
                                            >
                                              {statusOptions.map((option) => (
                                                <MenuItem key={option} value={option}>{option}</MenuItem>
                                              ))}
                                            </Select>
                                            )
                                          : (item.status || '-')}
                                      </TableCell>
                                      <TableCell>{item.dataAlteracao || '-'}</TableCell>
                                      <TableCell>{item.material || '-'}</TableCell>
                                      <TableCell>{item.quantidade || '-'}</TableCell>
                                      <TableCell width={280}>
                                        {canEditObs
                                          ? (
                                            <TextField
                                              size='small'
                                              fullWidth
                                              value={item.obs || ''}
                                              onChange={(e) => updateRowField(item.id, 'obs', e.target.value)}
                                            />
                                            )
                                          : (item.obs || '-')}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}

                {groupedOrders.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10}>
                      <Stack alignItems='center' py={2}>
                        <Typography color='text.secondary'>Sem Pedidos</Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {['PedidosOeMGerencial', 'PedidosOeMTecnico', 'Adm'].some(a => autorizacoes.includes(a)) && (
          <FormControl sx={{ marginTop: '30px' }}>
            <SubmitButton isSubmit={isSubmit}>Atualizar Pedidos</SubmitButton>
          </FormControl>
        )}
      </form>
    </Box>
  );
};

export default OrdersManager;
